import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, Clock, RefreshCw, Radio } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusIcon = ({ status }) => {
  if (status === 'running') return <Loader2 className="h-4 w-4 animate-spin text-wt-gold" />;
  if (status === 'done') return <CheckCircle2 className="h-4 w-4 text-green-400" />;
  if (status === 'error') return <XCircle className="h-4 w-4 text-red-400" />;
  return <Clock className="h-4 w-4 text-wt-text-muted" />;
};

export const ChannelSyncDialog = ({ open, onOpenChange, onCompleted }) => {
  const [run, setRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);
  const completedNotified = useRef(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/channel-sync/latest`, { headers: authHeaders() });
      const r = res.data?.run;
      if (r) setRun(r);
      if (r && r.status !== 'running') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (r.status === 'completed' && !completedNotified.current) {
          completedNotified.current = true;
          onCompleted && onCompleted(r);
        }
      }
    } catch (e) { /* keep polling */ }
  }, [onCompleted]);

  const start = useCallback(async () => {
    setStarting(true);
    completedNotified.current = false;
    try {
      const res = await axios.post(`${API}/api/channel-sync/start`, {}, { headers: authHeaders() });
      setRun(res.data.run);
      if (res.data.already_running) toast.info('A sync is already running — showing its progress');
      if (!pollRef.current) pollRef.current = setInterval(poll, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start channel sync');
      onOpenChange(false);
    } finally {
      setStarting(false);
    }
  }, [poll, onOpenChange]);

  useEffect(() => {
    if (open) {
      start();
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const running = run?.status === 'running';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-wt-card border-wt-border" data-testid="channel-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-wt-text-primary">
            <RefreshCw className={`h-5 w-5 text-wt-gold ${running || starting ? 'animate-spin' : ''}`} />
            Channel Order Sync
          </DialogTitle>
        </DialogHeader>

        {!run && (
          <div className="py-6 text-center text-sm text-wt-text-muted" data-testid="channel-sync-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Detecting connected channels...
          </div>
        )}

        {run && (
          <div className="space-y-1" data-testid="channel-sync-rows">
            {(run.channels || []).map((ch) => (
              <div key={ch.key} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-wt-subtle"
                   data-testid={`sync-row-${ch.channel}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  {ch.channel === 'carrefour' && ch.status === 'done'
                    ? <Radio className="h-4 w-4 text-green-400" />
                    : <StatusIcon status={ch.status} />}
                  <span className="text-sm text-wt-text-primary truncate">{ch.label}</span>
                </div>
                <span className={`text-xs ml-3 text-right ${ch.status === 'error' ? 'text-red-400' : 'text-wt-text-muted'}`}
                      data-testid={`sync-status-${ch.channel}`}>
                  {ch.status === 'queued' && 'Waiting...'}
                  {ch.status === 'running' && 'Syncing...'}
                  {ch.status === 'done' && (ch.message || 'Done')}
                  {ch.status === 'error' && (ch.error || 'Failed')}
                </span>
              </div>
            ))}

            <div className="pt-3 flex items-center justify-between">
              <p className="text-xs text-wt-text-muted">
                {running
                  ? 'Runs in background — you can close this window.'
                  : run.status === 'completed'
                    ? `Finished — ${run.total_ingested ?? 0} order(s) brought in.`
                    : run.status === 'stale' ? 'Last run did not finish (server restarted).' : ''}
              </p>
              {!running && (
                <Button size="sm" variant="outline" onClick={start} disabled={starting}
                        className="border-wt-border text-wt-text-primary"
                        data-testid="channel-sync-again-btn">
                  Sync Again
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
